import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  useTheme,
  useMediaQuery,
  Chip,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search,
  Dumbbell,
  X,
  ChevronRight,
  Globe
} from 'lucide-react';
import { FixedSizeList as List } from 'react-window';
import agtfitService from '../../service/agtfitService';
import ExerciseViewDialog from '../exercisesVisual/ExerciseViewDialog';

// Color principal
const FIT_ORANGE = '#FF6B00';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Estilos con la nueva estética similar a CheckIn.js
const ExplorerContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

// Estilo para diálogos estilo Apple como en CheckIn.js
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    [theme.breakpoints.up('sm')]: {
      borderRadius: '20px',
    },
    [theme.breakpoints.down('sm')]: {
      borderRadius: '0px',
    },
    boxShadow: theme.palette.actualMode === 'wallpaper'
      ? 'none'
      : (theme.palette.mode === 'dark'
        ? '0 20px 60px rgba(0, 0, 0, 0.5)'
        : '0 20px 60px rgba(0, 0, 0, 0.2)'),
    background: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bg
      : (theme.palette.mode === 'dark'
        ? 'rgba(30, 30, 32, 0.95)'
        : 'rgba(255, 255, 255, 1)'),
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
    overflow: 'hidden',
    // Asegurar que ocupa todo el espacio en pantalla completa
    height: theme.breakpoints.down('sm') ? '100%' : 'auto'
  }
}));

const SearchField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bgLight
      : (theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)'),
    backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    '& fieldset': {
      borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : 'transparent',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.actualMode === 'wallpaper'
        ? WALLPAPER_COLORS.border
        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'),
    },
    '&.Mui-focused fieldset': {
      borderColor: FIT_ORANGE,
    },
  }
}));

// Selector de idioma estilizado
const LanguageSelector = styled(FormControl)(({ theme }) => ({
  minWidth: 80,
  marginLeft: theme.spacing(1),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bgLight
      : (theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)'),
    backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    '& fieldset': {
      borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : 'transparent',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.actualMode === 'wallpaper'
        ? WALLPAPER_COLORS.border
        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'),
    },
    '&.Mui-focused fieldset': {
      borderColor: FIT_ORANGE,
    },
  },
  '& .MuiSelect-select': {
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 12,
    paddingRight: 12,
    display: 'flex',
    alignItems: 'center',
  }
}));

const FilterChip = styled(Box)(({ theme, selected }) => ({
  margin: theme.spacing(0.5),
  padding: '6px 12px',
  borderRadius: 12,
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  backgroundColor: selected
    ? (theme.palette.actualMode === 'wallpaper'
      ? 'rgba(255, 107, 0, 0.5)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(255, 107, 0, 0.7)'
        : 'rgba(255, 107, 0, 0.1)'))
    : (theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bgLight
      : (theme.palette.mode === 'dark'
        ? 'rgba(50, 50, 52, 0.8)'
        : 'rgba(245, 245, 250, 0.8)')),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  color: selected
    ? (theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.text
      : (theme.palette.mode === 'dark'
        ? '#FFFFFF'
        : FIT_ORANGE))
    : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: selected
      ? (theme.palette.actualMode === 'wallpaper'
        ? 'rgba(255, 107, 0, 0.6)'
        : (theme.palette.mode === 'dark'
          ? 'rgba(255, 107, 0, 0.8)'
          : 'rgba(255, 107, 0, 0.2)'))
      : (theme.palette.actualMode === 'wallpaper'
        ? 'rgba(255,107,0,0.15)'
        : (theme.palette.mode === 'dark'
          ? 'rgba(60, 60, 62, 0.8)'
          : 'rgba(235, 235, 240, 0.8)')),
  },
  display: 'inline-block'
}));

// MODIFICADO: Eliminada la sombra y elevación, agregado borde en modo claro
const ExerciseCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark'
      ? 'rgba(30, 30, 32, 0.8)'
      : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper'
    ? `1px solid ${WALLPAPER_COLORS.border}`
    : (theme.palette.mode === 'dark'
      ? '1px solid rgba(255,255,255,0.08)'
      : '1px solid rgba(0,0,0,0.1)'),
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(255,107,0,0.15)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(40, 40, 42, 0.8)'
        : 'rgba(250, 250, 250, 1)'),
  }
}));

// MODIFICADO: Asegurando que el IconBox sea perfectamente circular con aspect ratio
const IconBox = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  aspectRatio: '1/1',
  borderRadius: '50%',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 107, 0, 0.15)' : 'rgba(255, 107, 0, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
  color: FIT_ORANGE,
  flexShrink: 0,
  minWidth: 40,
  minHeight: 40,
}));

// Contenedor de imagen/video con tamaño fijo
const MediaBox = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '8px',
  marginRight: theme.spacing(2),
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
}));

// Contenedor principal para el área de ejercicios
const MainContentArea = styled(Box)(({ theme }) => ({
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden', // Importante: previene scroll adicional
}));

// Nuevo NavigationBar estilizado con ancho adaptable
const StyledTabs = styled(Box)(({ theme }) => ({
  minHeight: '40px',
  height: '40px',
  background: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark'
      ? 'rgba(40, 40, 42, 0.94)'
      : 'rgba(242, 242, 247, 1)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  padding: '6px',
  borderRadius: '24px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  maxWidth: '350px',
  margin: '0 auto',
}));

// TabIndicator modificado para usar transform
const TabIndicator = styled('div')(({ theme }) => ({
  position: 'absolute',
  height: '28px',
  borderRadius: '24px',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'rgba(255, 107, 0, 0.2)'
    : (theme.palette.mode === 'dark'
      ? 'rgba(118, 118, 128, 0.24)'
      : '#FFFFFF'),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: 0,
  boxShadow: theme.palette.actualMode === 'wallpaper'
    ? 'none'
    : (theme.palette.mode === 'dark'
      ? 'none'
      : '0 2px 8px rgba(0, 0, 0, 0.04)'),
}));

// StyledTab con ancho adaptable al contenido
const StyledTab = styled(Box)(({ theme, selected }) => ({
  minHeight: '28px',
  height: '28px',
  minWidth: 'auto',
  padding: '0 16px',
  borderRadius: '24px',
  color:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.7)'
      : '#666666',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  zIndex: 1,
  transition: 'all 0.2s',
  backgroundColor: 'transparent',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  ...(selected && {
    backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  }),
  '&:hover:not(.Mui-selected)': {
    backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundColor: 'transparent',
  },
}));

// Área de filtros con scroll horizontal
const FilterScrollArea = styled(Box)(({ theme }) => ({
  overflowX: 'auto',
  overflowY: 'hidden',
  whiteSpace: 'nowrap',
  WebkitOverflowScrolling: 'touch',
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none'
  },
  padding: '4px 0',
}));

// MODIFICADO: Reducido el tamaño del texto en etiquetas de tipo de media
const MediaTypeChip = styled(Chip)(({ theme }) => ({
  height: '20px',
  fontSize: '8px', // Reducido de 11px a 10px
  fontWeight: 500,
  backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
  color: '#FFFFFF',
  '& .MuiChip-label': {
    padding: '0 6px',
  },
  '& .MuiChip-icon': {
    color: '#FFFFFF',
    marginLeft: '4px',
  }
}));

// Función para capitalizar primera letra
const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

const MUSCLES = [
  'ABDOMINALS', 'ABDUCTORS', 'ADDUCTORS', 'BICEPS', 'LOWER BACK', 'UPPER BACK',
  'CHEST', 'CALVES', 'FOREARMS', 'GLUTES', 'HAMSTRINGS', 'LATS',
  'QUADRICEPS', 'SHOULDERS', 'TRICEPS', 'TRAPS', 'FULL BODY'
];

const EQUIPMENT = [
  'NONE', 'BARBELL', 'DUMBBELL', 'KETTLEBELL', 'MACHINE', 'PLATE',
  'RESISTANCE BAND', 'SUSPENSION', 'OTHER'
];

// Idiomas disponibles
const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' }
];

// Componente principal
const ExerciseExplorer = ({ open, onSelectExercise, onClose, fromQuickStart = false }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchQuery, setSearchQuery] = useState('');
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMuscle, setSelectedMuscle] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedExerciseForView, setSelectedExerciseForView] = useState(null);
  // Nuevo estado para el idioma seleccionado
  const [language, setLanguage] = useState('en');
  
  // Referencias para el indicador de pestañas
  const tabsContainerRef = useRef(null);
  const tabRefs = useRef([]);
  if (tabRefs.current.length === 0) {
    tabRefs.current = Array(2).fill(null).map(() => ({ current: null }));
  }
  
  // Estado para el indicador de pestañas
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: '6px',
    width: '80px'
  });

  // Referencia para el contenedor principal para calcular altura
  const contentRef = useRef(null);
  const [listHeight, setListHeight] = useState(400);

  // MODIFICADO: Referencia para la lista virtualizada
  const listRef = useRef(null);

  // Buscar ejercicios con debounce
  const debouncedFetch = useRef(null);

  // Buscar ejercicios - MOVIDO ANTES DEL useEffect
  const fetchExercises = useCallback(async (query = '', muscle = '', equipment = '') => {
    setLoading(true);
    try {
      // Modificado para incluir el parámetro de idioma
      const response = await agtfitService.searchExerciseCatalog(query, muscle, equipment, language);

      if (response.success) {
        setExercises(response.data);
      } else {
        setError('Failed to load exercises');
      }
    } catch (err) {
      setError('Error loading exercises');
      console.error('Error fetching exercises:', err);
    } finally {
      setLoading(false);
    }
  }, [language]); // Añadido language como dependencia

  // Actualizar altura de la lista virtualizada
  const updateListHeight = useCallback(() => {
    if (contentRef.current) {
      // Restar los elementos superiores para calcular espacio disponible
      const availableHeight = contentRef.current.clientHeight;
      setListHeight(availableHeight);

      // AGREGADO: Forzar re-renderizado de la lista virtualizada cuando cambia la altura
      if (listRef.current) {
        setTimeout(() => {
          listRef.current.resetAfterIndex(0);
        }, 50);
      }
    }
  }, []);

  // Cargar ejercicios al iniciar
  useEffect(() => {
    fetchExercises();

    // Ajustar el tamaño de la lista al montar el componente
    if (contentRef.current) {
      updateListHeight();
    }

    // Listener para ajustar el tamaño en cambios de ventana
    window.addEventListener('resize', updateListHeight);
    return () => window.removeEventListener('resize', updateListHeight);
  }, [fetchExercises, updateListHeight]); // eslint-disable-line react-hooks/exhaustive-deps

  // Actualizar la posición del indicador cuando cambia la pestaña activa
  useEffect(() => {
    const updateIndicator = () => {
      if (tabRefs.current[activeTab]?.current) {
        const tabElement = tabRefs.current[activeTab].current;
        const tabsContainer = tabsContainerRef.current;

        if (tabElement && tabsContainer) {
          // Obtener la posición del contenedor de pestañas
          const containerRect = tabsContainer.getBoundingClientRect();
          // Obtener la posición de la pestaña activa
          const tabRect = tabElement.getBoundingClientRect();

          // Calcular la posición relativa de la pestaña dentro del contenedor
          const left = tabRect.left - containerRect.left;

          setIndicatorStyle({
            left: `${left}px`,
            width: `${tabRect.width}px`
          });
        }
      }
    };

    // Pequeño retraso para asegurar que los elementos estén renderizados
    const timer = setTimeout(updateIndicator, 50);
    return () => clearTimeout(timer);
  }, [activeTab, tabRefs]);

  // Filtrar por músculo
  const handleMuscleFilter = useCallback((muscle) => {
    if (selectedMuscle === muscle) {
      setSelectedMuscle(null);
      fetchExercises(searchQuery);
    } else {
      setSelectedMuscle(muscle);
      fetchExercises(searchQuery, muscle, selectedEquipment);
    }
  }, [selectedMuscle, searchQuery, selectedEquipment, fetchExercises]);

  // Filtrar por equipamiento
  const handleEquipmentFilter = useCallback((equipment) => {
    if (selectedEquipment === equipment) {
      setSelectedEquipment(null);
      fetchExercises(searchQuery, selectedMuscle);
    } else {
      setSelectedEquipment(equipment);
      fetchExercises(searchQuery, selectedMuscle, equipment);
    }
  }, [selectedEquipment, searchQuery, selectedMuscle, fetchExercises]);

  // Manejar búsqueda con debounce
  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    
    // Cancelar el debounce anterior si existe
    if (debouncedFetch.current) {
      clearTimeout(debouncedFetch.current);
    }
    
    // Crear nuevo debounce
    debouncedFetch.current = setTimeout(() => {
      fetchExercises(query, selectedMuscle, selectedEquipment);
    }, 300);
  };

  // Manejar cambio de idioma
  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    setLanguage(newLanguage);
    // Volver a cargar los ejercicios con el nuevo idioma
    fetchExercises(searchQuery, selectedMuscle, selectedEquipment);
  };

  // Seleccionar ejercicio
  const handleSelectExercise = useCallback((exercise) => {
    if (fromQuickStart) {
      // Si estamos en QuickStart, mostrar el diálogo de vista de ejercicio
      setSelectedExerciseForView(exercise);
      // No cerramos el explorador
    } else if (onSelectExercise) {
      // Comportamiento normal para otros componentes
      onSelectExercise(exercise);
    }
  }, [onSelectExercise, fromQuickStart]);

  // Función para cerrar el diálogo de vista de ejercicio
  const handleCloseExerciseView = () => {
    setSelectedExerciseForView(null);
  };

  // Cambiar entre pestañas
  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
  };

  // MODIFICADO: Nueva función para renderizar la imagen o icono del ejercicio
  const renderExerciseMedia = useCallback((exercise) => {
    // Si tiene imagen, mostrarla
    if (exercise.imageUrl) {
      return (
        <MediaBox>
          <img 
            src={exercise.imageUrl} 
            alt={exercise.name} 
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover' 
            }}
            loading="lazy"
          />
        </MediaBox>
      );
    }
    // Si no tiene imagen, mostrar el icono por defecto (Dumbbell)
    else {
      return (
        <IconBox>
          <Dumbbell size={20} />
        </IconBox>
      );
    }
  }, []);

  // NUEVO: Función para determinar y mostrar el tipo de media
  const getMediaType = useCallback((exercise) => {
    if (exercise.videoUrl) {
      return {
        label: 'Video'
      };
    } else if (exercise.imageUrl) {
      return {
        label: 'Image'
      };
    }
    return null;
  }, []);

  // Definición de las pestañas
  const tabs = useMemo(() => [
    { id: 0, label: 'Muscles' },
    { id: 1, label: 'Equipment' }
  ], []);

  // MODIFICADO: Ajustada la altura del elemento y márgenes para evitar superposición
  const itemSize = useMemo(() => {
    // En pantallas pequeñas, aumentamos la altura del elemento para que no se superponga
    return fullScreen ? 110 : 88;
  }, [fullScreen]);

  // Función para mostrar el nombre del ejercicio según el idioma seleccionado
  const getExerciseName = useCallback((exercise) => {
    if (language === 'es' && exercise.nameEs) {
      return exercise.nameEs;
    }
    return exercise.name;
  }, [language]);

  // Función para mostrar la descripción según el idioma seleccionado (Unused function)
  // const getExerciseDescription = useCallback((exercise) => {
  //   if (language === 'es' && exercise.descriptionEs) {
  //     return exercise.descriptionEs;
  //   }
  //   return exercise.description;
  // }, [language]);

  // Componente de elemento de lista virtualizada
  const ExerciseItem = useCallback(({ index, style }) => {
    const exercise = exercises[index];
    const mediaType = getMediaType(exercise);
    
    return (
      <div style={{
        ...style,
        // MODIFICADO: Aumentamos padding para evitar superposición
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 8,
        paddingRight: 8,
      }}>
        <ExerciseCard 
          onClick={() => handleSelectExercise(exercise)}
          elevation={0} // Eliminada la elevación
        >
          <CardContent sx={{ 
            p: 2, 
            '&:last-child': { pb: 2 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
              {renderExerciseMedia(exercise)}
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" fontWeight={500} noWrap>
                    {getExerciseName(exercise)}
                  </Typography>
                  {mediaType && (
                    <MediaTypeChip 
                      label={mediaType.label}
                      size="small"
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {exercise.primaryMuscle && capitalizeFirstLetter(exercise.primaryMuscle)} 
                  {exercise.equipment && ` • ${capitalizeFirstLetter(exercise.equipment)}`}
                  {!exercise.equipment && ' • No Equipment'}
                </Typography>
              </Box>
            </Box>
            <ChevronRight size={18} color={theme.palette.text.secondary} sx={{ flexShrink: 0, ml: 1 }} />
          </CardContent>
        </ExerciseCard>
      </div>
    );
  }, [exercises, handleSelectExercise, renderExerciseMedia, getMediaType, theme.palette.text.secondary, getExerciseName]);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        px: 3,
        // Aumentar el padding vertical en móvil
        py: fullScreen ? 3 : 2,
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'}`
      }}>
        <Typography variant="h5" fontWeight={600}>
          Exercise Explorer
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Selector de idioma */}
          <LanguageSelector size="small">
            <Select
              value={language}
              onChange={handleLanguageChange}
              displayEmpty
              variant="outlined"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Globe size={16} style={{ marginRight: 8 }} />
                  {LANGUAGES.find(lang => lang.code === selected)?.name || 'Language'}
                </Box>
              )}
              size="small"
            >
              {LANGUAGES.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </LanguageSelector>
          
          <IconButton 
            onClick={onClose} 
            sx={{ 
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(240, 240, 245, 0.8)',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(70, 70, 72, 0.8)' : 'rgba(230, 230, 235, 0.8)',
              },
              // Aumentar el tamaño en móvil
              width: fullScreen ? 44 : 36,
              height: fullScreen ? 44 : 36,
              ml: 2
            }}
          >
            <X size={fullScreen ? 22 : 20} />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ 
        // Más espacio en los bordes en móvil
        px: fullScreen ? 4 : 3, 
        py: fullScreen ? 4 : 3,
        height: fullScreen ? 'calc(100% - 70px)' : '70vh', 
        display: 'flex', 
        flexDirection: 'column' 
      }}>
        <ExplorerContainer>
          <SearchField
            fullWidth
            placeholder={language === 'es' ? "Buscar ejercicios..." : "Search exercises..."}
            variant="outlined"
            value={searchQuery}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} color={theme.palette.text.secondary} />
                </InputAdornment>
              ),
            }}
          />

          {/* Nuevo NavigationBar estilo FitProfile */}
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 2 }}>
            <StyledTabs ref={tabsContainerRef}>
              <TabIndicator style={indicatorStyle} />
              {tabs.map((tab, index) => (
                <StyledTab 
                  key={tab.id}
                  selected={activeTab === tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  ref={tabRefs[index]}
                >
                  {language === 'es' && tab.id === 0 ? 'Músculos' : 
                   language === 'es' && tab.id === 1 ? 'Equipamiento' : 
                   tab.label}
                </StyledTab>
              ))}
            </StyledTabs>
          </Box>

          {/* Área de filtros con scroll horizontal */}
          <FilterScrollArea sx={{ mb: 3 }}>
            {activeTab === 0 ? (
              <>
                {MUSCLES.map((muscle) => (
                  <FilterChip
                    key={muscle}
                    selected={selectedMuscle === muscle}
                    onClick={() => handleMuscleFilter(muscle)}
                  >
                    {muscle.split(' ').map(word => capitalizeFirstLetter(word)).join(' ')}
                  </FilterChip>
                ))}
              </>
            ) : (
              <>
                {EQUIPMENT.map((equip) => (
                  <FilterChip
                    key={equip}
                    selected={selectedEquipment === equip}
                    onClick={() => handleEquipmentFilter(equip)}
                  >
                    {capitalizeFirstLetter(equip)}
                  </FilterChip>
                ))}
              </>
            )}
          </FilterScrollArea>

          <Divider sx={{ mb: 2 }} />

          {/* Contenedor principal para la lista de ejercicios */}
          <MainContentArea ref={contentRef}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress size={40} sx={{ color: FIT_ORANGE }} />
              </Box>
            ) : error ? (
              <Typography color="error" align="center" sx={{ p: 4 }}>
                {error}
              </Typography>
            ) : exercises.length === 0 ? (
              <Typography color="textSecondary" align="center" sx={{ p: 4 }}>
                {language === 'es' ? 
                  "No se encontraron ejercicios. Intenta con otros términos o filtros." : 
                  "No exercises found. Try different search terms or filters."}
              </Typography>
            ) : (
              // MODIFICADO: Lista virtualizada para mejor rendimiento con referencia para recalcular
              <List
                ref={listRef}
                height={listHeight}
                width="100%"
                itemCount={exercises.length}
                itemSize={itemSize} // Tamaño ajustado según pantalla
                overscanCount={5}
              >
                {ExerciseItem}
              </List>
            )}
          </MainContentArea>
          
          {/* Diálogo para ver detalles del ejercicio */}
          <ExerciseViewDialog
            open={Boolean(selectedExerciseForView)}
            onClose={handleCloseExerciseView}
            exercise={selectedExerciseForView}
            language={language} // Pasar el idioma seleccionado
          />
        </ExplorerContainer>
      </DialogContent>
    </StyledDialog>
  );
};

export default ExerciseExplorer;